import { useMutation, useSuspenseQuery } from "@apollo/client";
import { capitaliseFirstLetter } from "@shared/translations/src";
import { useState } from "react";
import { type z } from "zod";
import { BookingDocumentUploadForm } from "../BookingDocumentUploadForm";
import { DocumentTile } from "@/components/DocumentTile";
import { type documentUploadFormSchema } from "@/components/DocumentUploadForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { graphql } from "@/graphql";
import { useToast } from "@/hooks/use-toast";
import { removeNothings } from "@/lib/utils/arrays";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";

export function BookingDocuments({ bookingId }: { bookingId: string }) {
  const { data } = useSuspenseQuery(supplyChainBookingDocumentsQuery, {
    variables: { booking: bookingId, status: ["ACTIVE"] },
  });

  const documents = removeNothings((data?.supplyChainBookingDocuments?.edges || []).map((edge) => edge?.node));
  const { toast } = useToast();
  const [uploadSupplyChainBookingDocument] = useMutation(uploadSupplyChainBookingDocumentMutation);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [archiveDocument] = useMutation(archiveSupplyChainBookingDocuments);

  const handleDelete = async (documentId: string) => {
    try {
      const result = await archiveDocument({
        variables: {
          input: {
            documents: [documentId],
          },
        },
        refetchQueries: [supplyChainBookingDocumentsQuery],
      });

      const couldNotArchiveErrors = result.data?.archiveSupplyChainBookingDocuments?.couldNotArchive;

      if (couldNotArchiveErrors && couldNotArchiveErrors[0]?.reason) {
        toast({
          title: `Error archiving document`,
          description: couldNotArchiveErrors[0]?.reason
            ? capitaliseFirstLetter(couldNotArchiveErrors[0]?.reason)
            : "Unknown error",
          variant: "destructive",
        });

        return;
      }

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: `Error deleting document`,
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      toast({
        title: "Success",
        description: `Document deleted successfully`,
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Error deleting document",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (values: z.infer<typeof documentUploadFormSchema>) => {
    try {
      if (!bookingId) {
        toast({
          title: "Error uploading document",
          description: "Booking ID is missing",
          variant: "destructive",
        });

        return;
      }

      const result = await uploadSupplyChainBookingDocument({
        variables: {
          input: {
            file: values.file,
            name: values.name,
            type: values.type,
            booking: bookingId,
          },
        },
        refetchQueries: [supplyChainBookingDocumentsQuery],
      });

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: `Error uploading document.`,
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      setIsDialogOpen(false);

      toast({
        title: "Success",
        description: `Document ${values.name} uploaded successfully`,
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Error uploading document",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <DocumentTile
        documents={documents}
        title={`Documents (${documents.length})`}
        onDelete={handleDelete}
        onUpload={() => {
          setIsDialogOpen(true);
        }}
      />
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription className="sr-only">
              Upload your document by dragging and dropping or selecting a file.
            </DialogDescription>
          </DialogHeader>

          <BookingDocumentUploadForm onSubmit={handleSubmit} onCancel={() => setIsDialogOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}

export const supplyChainBookingDocumentsQuery = graphql(`
  query SupplyChainBookingDocuments($booking: ID!, $status: [SupplyChainBookingDocumentStatusEnumType!]) {
    supplyChainBookingDocuments(booking: $booking, status: $status) {
      numSupplyChainBookingDocuments
      edges {
        node {
          id
          name
          url
          type {
            id
            label
          }
        }
      }
    }
  }
`);

export const uploadSupplyChainBookingDocumentMutation = graphql(`
  mutation UploadSupplyChainBookingDocument($input: UploadSupplyChainBookingDocumentInput!) {
    uploadSupplyChainBookingDocument(input: $input) {
      document {
        id
        name
        type {
          id
          label
        }
      }
    }
  }
`);

export const archiveSupplyChainBookingDocuments = graphql(`
  mutation ArchiveSupplyChainBookingDocument($input: ArchiveSupplyChainBookingDocumentsInput!) {
    archiveSupplyChainBookingDocuments(input: $input) {
      couldNotArchive {
        id
        reason
      }
      documents {
        id
      }
    }
  }
`);
